import React, { useCallback, useId, useState } from 'react';
import type { ReactNode, Ref } from 'react';

import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';
import { css } from '@compiled/react';

import { SectionCountBadge } from '../SectionCountBadge.tsx';

// Five 52px rows, their 4px gaps, and half a row so the cut-off row signals the scroll.
const MAX_BODY_HEIGHT = '306px';

const wrapperStyles = css({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	paddingBlock: token('space.200'),
	paddingInline: token('space.200'),
	gap: token('space.250'),
	borderBlockEndColor: token('color.border'),
	borderBlockEndStyle: 'solid',
	borderBlockEndWidth: token('border.width'),
	backgroundColor: token('elevation.surface'),
	boxSizing: 'border-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- only removes the trailing divider on the last section
	'&:last-of-type': {
		borderBlockEndWidth: 0,
	},
});

const styles = cssMap({
	header: {
		display: 'flex',
		alignItems: 'center',
		alignSelf: 'stretch',
		gap: token('space.050'),
		height: '32px',
		paddingBlock: token('space.0'),
		paddingInline: token('space.0'),
		backgroundColor: 'transparent',
		cursor: 'pointer',
		font: token('font.body'),
		color: token('color.text'),
		textAlign: 'left',
		borderRadius: token('radius.small'),
	},
	iconBefore: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		paddingBlock: token('space.050'),
		paddingInline: token('space.050'),
		boxSizing: 'border-box',
		flexShrink: 0,
	},
	titleGroup: {
		display: 'flex',
		flex: 1,
		minWidth: 0,
		alignItems: 'center',
		gap: token('space.100'),
	},
	titleText: {
		minWidth: 0,
		font: token('font.heading.xsmall'),
		color: token('color.text'),
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	trailing: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.100'),
		flexShrink: 0,
	},
	chevron: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		paddingBlock: token('space.050'),
		paddingInline: token('space.050'),
		boxSizing: 'border-box',
		flexShrink: 0,
	},
	// Grid with max-content rows, so rows keep their height and the body scrolls instead.
	// Rows clip their own hover paint with `overflow: hidden`, which zeroes their automatic
	// minimum size, so `auto` tracks (and a flex column) squash them to fit the cap once an
	// ancestor is also height-constrained.
	body: {
		display: 'grid',
		gridAutoRows: 'max-content',
		gap: token('space.050'),
		maxHeight: MAX_BODY_HEIGHT,
		overflowY: 'auto',
	},
});

export type ConnectionsByLineSectionShellProps = {
	title: string;
	/** Shown in a badge beside the title, zero included. Omitted only when absent. */
	count?: number;
	/** Renders the count as `count+`. Ignored when the count is zero, since there is no count to qualify. */
	hasMore?: boolean;
	iconBefore?: ReactNode;
	/** Rendered between the title and the chevron — typically an icon stack. */
	trailing?: ReactNode;
	children: ReactNode;
	/** The scrolling body element, for callers that observe scroll position within it. */
	bodyRef?: Ref<HTMLDivElement>;
	/** When true, the body is shown on first render. Every section stays collapsed unless it passes this. */
	defaultExpanded?: boolean;
	/**
	 * Controlled expanded state. Pass this with `onToggleExpanded` for a caller that owns
	 * the state (the modal lifts it so it survives switching to Rovo and back); omit both
	 * and the shell owns the state itself.
	 */
	isExpanded?: boolean;
	/** Called on a header click when `isExpanded` is controlled. */
	onToggleExpanded?: () => void;
	testId?: string;
};

const ChevronIcon = ({ isExpanded }: { isExpanded: boolean }): React.JSX.Element =>
	isExpanded ? (
		<ChevronDownIcon label="" color={token('color.icon.subtle')} spacing="none" />
	) : (
		<ChevronRightIcon label="" color={token('color.icon.subtle')} spacing="none" />
	);

/**
 * The section's expanded state and the handler that flips it, in one of two modes — the
 * same controlled/uncontrolled split as a React `<input>`:
 *
 * - Controlled: `controlledExpanded` is passed, so the caller owns the state and the
 *   handler defers to `onToggle`. The modal uses this to hold the state above the swap to
 *   Rovo, so a section stays expanded across it.
 * - Uncontrolled: neither is passed, so the hook owns the state (starting from
 *   `defaultExpanded`, collapsed unless a section opts in) and the handler flips it.
 *   The panel and popup use this; "At a glance" is the opt-in.
 *
 * Exported because `RelatedPeopleSection` needs the same split without the rest of the shell:
 * it is a `PressableGroup`, not a collapsible body, so it discloses extra rows rather than
 * hiding all of them. Keep one implementation of the split rather than two.
 */
export const useExpandedState = (
	controlledExpanded: boolean | undefined,
	onToggle: (() => void) | undefined,
	defaultExpanded = false,
): readonly [boolean, () => void] => {
	const [uncontrolledExpanded, setUncontrolledExpanded] = useState(defaultExpanded);
	const isControlled = controlledExpanded !== undefined;

	const toggle = useCallback(() => {
		if (isControlled) {
			onToggle?.();
		} else {
			setUncontrolledExpanded((prev) => !prev);
		}
	}, [isControlled, onToggle]);

	return [isControlled ? controlledExpanded : uncontrolledExpanded, toggle];
};

/**
 * Chrome for an expandable preview-panel section, with no opinion on what the body
 * contains: `ConnectionsByLineSection` wraps this to render smart-link rows, other
 * callers supply their own.
 *
 * The body is capped and scrolls, so a long section cannot push the ones below it
 * out of view.
 */
export const ConnectionsByLineSectionShell = ({
	title,
	count,
	hasMore = false,
	iconBefore,
	trailing,
	children,
	bodyRef,
	defaultExpanded = false,
	isExpanded: controlledExpanded,
	onToggleExpanded,
	testId,
}: ConnectionsByLineSectionShellProps): React.JSX.Element => {
	const [isExpanded, handleClick] = useExpandedState(
		controlledExpanded,
		onToggleExpanded,
		defaultExpanded,
	);

	const id = useId();
	const headerId = `${id}-header`;
	const bodyId = `${id}-body`;

	return (
		<div css={wrapperStyles} data-testid={testId}>
			<Pressable
				id={headerId}
				aria-expanded={isExpanded}
				aria-controls={bodyId}
				onClick={handleClick}
				xcss={styles.header}
				testId={testId ? `${testId}--header` : undefined}
			>
				{iconBefore ? <span css={styles.iconBefore}>{iconBefore}</span> : null}
				<span css={styles.titleGroup}>
					<span css={styles.titleText}>{title}</span>
					{count !== undefined ? (
						<SectionCountBadge count={count} hasMore={hasMore && count > 0} />
					) : null}
				</span>
				<span css={styles.trailing}>
					{trailing}
					<span css={styles.chevron}>
						<ChevronIcon isExpanded={isExpanded} />
					</span>
				</span>
			</Pressable>
			{isExpanded ? (
				<Box
					ref={bodyRef}
					id={bodyId}
					role="region"
					aria-labelledby={headerId}
					// Reachable for keyboard-only scrolling.
					tabIndex={0}
					xcss={styles.body}
					testId={testId ? `${testId}--body` : undefined}
				>
					{children}
				</Box>
			) : null}
		</div>
	);
};
